import {
  ContentSwitcher,
  Field,
  IconButton,
  Modal,
  NumberInput,
  SingleSelectDropdown,
} from "@carbon6/c6-frontend-design";
import { useId, useState, type ReactNode, type SyntheticEvent } from "react";
import { hatcheryDropdownModes } from "@/app/theme";
import { HatModalHeader } from "@/components/modal";
import { channelTitle, renderGroup, type ChannelStructureIndex } from "../parquetChannels";
import styles from "./GripperSettingsDialog.module.css";

/** 적용된 그리퍼 판정 설정. 적용 전에는 구간을 바꾸지 않는다. */
export type GripperSettings = {
  /** 판정에 쓸 채널 키. */
  source: string;
  threshold: number;
  /** 임계값보다 큰 쪽이 열림인지 여부. 숫자의 크기만으로는 알 수 없어서 사용자가 정한다. */
  openAbove: boolean;
};

export type GripperSettingsDialogProps = {
  /** `Left 그리퍼` 처럼 이 행을 가리키는 이름. */
  label: string;
  /** 고를 수 있는 판정 소스 채널 키. */
  sources: readonly string[];
  channels: ChannelStructureIndex;
  /** 지금 적용된 값. 없으면 기본 소스만 선택한 빈 초안으로 연다. */
  applied: GripperSettings | null;
  defaultSource: string;
  onClose: () => void;
  onApply: (settings: GripperSettings) => void;
  /** 적용된 구간을 지운다. 적용 전이면 전달하지 않는다. */
  onClear?: () => void;
};

/**
 * 그리퍼 판정 설정 모달.
 *
 * 사용자는 초안을 편집한다. `적용` 전에는 실제 구간을 바꾸지 않는다. 설정을 모달에 두는
 * 이유는 다음과 같다.
 * - `<details>` + absolute 팝오버는 `container-type: inline-size` 와 `overflow: hidden` 조상에
 *   잘렸다.
 * - 그 팝오버는 c6 컨트롤을 raw `select`/`input` 으로 다시 구현했다.
 */
export function GripperSettingsDialog({
  label,
  sources,
  channels,
  applied,
  defaultSource,
  onClose,
  onApply,
  onClear,
}: GripperSettingsDialogProps): ReactNode {
  const titleId = useId();
  const [source, setSource] = useState(applied?.source ?? defaultSource);
  const [threshold, setThreshold] = useState<number | null>(applied?.threshold ?? null);
  const [openAbove, setOpenAbove] = useState(applied?.openAbove ?? true);
  const valid = threshold !== null && Number.isFinite(threshold);

  function handleSubmit(event: SyntheticEvent<HTMLFormElement, SubmitEvent>): void {
    event.preventDefault();
    if (!valid) return;
    onApply({ source, threshold, openAbove });
  }

  return (
    <Modal open onClose={onClose} size="md" aria-labelledby={titleId}>
      <HatModalHeader title={`${label} 판정 설정`} titleId={titleId} close />
      <form className={styles.form} onSubmit={handleSubmit}>
        <Field label="기준 채널" layout="stack">
          <SingleSelectDropdown
            label="기준 채널"
            mode={hatcheryDropdownModes.default}
            size="sm"
            fullWidth
            menuWidth="matchTrigger"
            value={source}
            onValueChange={setSource}
            options={sources.map((name) => ({
              value: name,
              label: `${channelTitle(name, channels.get(name))} · ${
                renderGroup(channels.get(name), name) === "observation" ? "Observation" : "Action"
              }`,
            }))}
          />
        </Field>
        <Field label="임계값" layout="stack">
          <NumberInput
            aria-label={`${label} 임계값`}
            value={threshold}
            onChange={(value) => setThreshold(value)}
            mode="decimal"
            maxDecimalPlaces={6}
            inputSize="sm"
          />
        </Field>
        <Field label="열림 방향" layout="stack">
          <ContentSwitcher
            label={`${label} 열림 방향`}
            size="sm"
            value={openAbove ? "above" : "below"}
            onValueChange={(value) => setOpenAbove(value === "above")}
            options={[
              { value: "above", label: "임계값 이상이 열림" },
              { value: "below", label: "임계값 이하가 열림" },
            ]}
          />
        </Field>
        <div className={styles.actions}>
          {onClear ? (
            <IconButton text="구간 지우기" type="button" variant="surface" onClick={onClear} />
          ) : null}
          <span className={styles.spacer} />
          <IconButton text="취소" type="button" variant="surface" onClick={onClose} />
          <IconButton text="적용" type="submit" variant="solid" disabled={!valid} />
        </div>
      </form>
    </Modal>
  );
}
